/**
 * Student details checkpoint at cart checkout (once per order).
 *
 * Intercepts the cart "Checkout" button. If the cart contains a course
 * (non-gift-card item) and the student attributes aren't set yet, it shows a
 * modal collecting Student Name / Grade / Birthday, saves them as cart
 * attributes via /cart/update.js, then proceeds to checkout. Express buttons
 * are hidden for course carts (cart-summary.liquid), so this can't be skipped.
 *
 * Stores order-level cart attributes: "Student Name", "Student Grade",
 * "Student Birthday" — read by the backend (Veracross registration sync).
 */
(() => {
  if (window.__ahsoStudentCheckpoint) return;
  window.__ahsoStudentCheckpoint = true;

  const KEYS = { name: 'Student Name', grade: 'Student Grade', birthday: 'Student Birthday' };
  let proceeding = false;

  async function getCart() {
    const res = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
    if (!res.ok) throw new Error('cart fetch failed: ' + res.status);
    return res.json();
  }

  function hasStudent(attrs) {
    return Boolean(attrs && attrs[KEYS.name] && attrs[KEYS.grade] && attrs[KEYS.birthday]);
  }

  function goToCheckout() {
    proceeding = true;
    window.location.href = '/checkout';
  }

  function injectStyles() {
    if (document.getElementById('ahso-sc-styles')) return;
    const s = document.createElement('style');
    s.id = 'ahso-sc-styles';
    s.textContent = `
      .ahso-sc-overlay { position: fixed; inset: 0; z-index: 2147483000; display: flex;
        align-items: center; justify-content: center; background: rgba(0,0,0,.5); padding: 1rem; }
      .ahso-sc-overlay[hidden] { display: none; }
      .ahso-sc-modal { background: #fff; color: #111; width: 100%; max-width: 420px;
        border-radius: 12px; padding: 1.5rem; box-shadow: 0 20px 60px rgba(0,0,0,.3);
        font-family: inherit; }
      .ahso-sc-modal h2 { margin: 0 0 .25rem; font-size: 1.15rem; }
      .ahso-sc-modal p.ahso-sc-sub { margin: 0 0 1rem; font-size: .85rem; color: #555; }
      .ahso-sc-field { margin-bottom: .8rem; }
      .ahso-sc-field label { display: block; font-size: .78rem; margin-bottom: .25rem; color: #333; }
      .ahso-sc-field input, .ahso-sc-field select { width: 100%; padding: .6rem .7rem;
        font-size: .95rem; border: 1px solid #ccc; border-radius: 8px; background: #fff; color: #111; }
      .ahso-sc-actions { display: flex; gap: .6rem; margin-top: 1.2rem; }
      .ahso-sc-actions button { flex: 1; padding: .7rem 1rem; border-radius: 8px; cursor: pointer;
        font-size: .95rem; border: 1px solid #111; }
      .ahso-sc-continue { background: #111; color: #fff; }
      .ahso-sc-cancel { background: #fff; color: #111; }
      .ahso-sc-error { color: #b00020; font-size: .8rem; margin-top: .5rem; min-height: 1rem; }
    `;
    document.head.appendChild(s);
  }

  function buildModal(attrs, mode) {
    const isEdit = mode === 'edit';
    injectStyles();
    let overlay = document.getElementById('ahso-sc-overlay');
    if (overlay) overlay.remove();
    overlay = document.createElement('div');
    overlay.id = 'ahso-sc-overlay';
    overlay.className = 'ahso-sc-overlay';
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');
    overlay.innerHTML = `
      <div class="ahso-sc-modal">
        <h2>Who is this enrollment for?</h2>
        <p class="ahso-sc-sub">Please enter the student's details before checkout.</p>
        <div class="ahso-sc-field">
          <label for="ahso-sc-name">Student's full name</label>
          <input id="ahso-sc-name" type="text" autocomplete="off" />
        </div>
        <div class="ahso-sc-field">
          <label for="ahso-sc-grade">Grade</label>
          <select id="ahso-sc-grade">
            <option value="">Select grade…</option>
            <option value="9">9th grade</option>
            <option value="10">10th grade</option>
            <option value="11">11th grade</option>
            <option value="12">12th grade</option>
          </select>
        </div>
        <div class="ahso-sc-field">
          <label for="ahso-sc-birthday">Birthday</label>
          <input id="ahso-sc-birthday" type="date" />
        </div>
        <div class="ahso-sc-error" aria-live="polite"></div>
        <div class="ahso-sc-actions">
          <button type="button" class="ahso-sc-cancel">Cancel</button>
          <button type="button" class="ahso-sc-continue">Continue to checkout</button>
        </div>
      </div>`;
    document.body.appendChild(overlay);

    const nameEl = overlay.querySelector('#ahso-sc-name');
    const gradeEl = overlay.querySelector('#ahso-sc-grade');
    const bdayEl = overlay.querySelector('#ahso-sc-birthday');
    const errEl = overlay.querySelector('.ahso-sc-error');
    if (attrs) {
      nameEl.value = attrs[KEYS.name] || '';
      gradeEl.value = attrs[KEYS.grade] || '';
      bdayEl.value = attrs[KEYS.birthday] || '';
    }
    if (isEdit) overlay.querySelector('h2').textContent = 'Edit student details';

    const close = () => overlay.remove();
    overlay.querySelector('.ahso-sc-cancel').addEventListener('click', close);
    overlay.addEventListener('click', (e) => { if (e.target === overlay) close(); });
    document.addEventListener('keydown', function esc(e) {
      if (e.key === 'Escape') { close(); document.removeEventListener('keydown', esc); }
    });

    const continueBtn = overlay.querySelector('.ahso-sc-continue');
    const defaultLabel = isEdit ? 'Save' : 'Continue to checkout';
    continueBtn.textContent = defaultLabel;
    continueBtn.addEventListener('click', async () => {
      if (continueBtn.disabled) return;
      const name = nameEl.value.trim();
      const grade = gradeEl.value.trim();
      const birthday = bdayEl.value.trim();
      if (!name || !grade || !birthday) {
        errEl.textContent = 'Please complete all three fields.';
        return;
      }
      errEl.textContent = '';
      continueBtn.disabled = true; continueBtn.textContent = 'Saving…';
      try {
        const res = await fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ attributes: {
            [KEYS.name]: name, [KEYS.grade]: grade, [KEYS.birthday]: birthday,
          } }),
        });
        if (!res.ok) throw new Error('cart update failed: ' + res.status);
        if (isEdit) window.location.reload();
        else goToCheckout();
      } catch (e) {
        errEl.textContent = 'Something went wrong. Please try again.';
        continueBtn.disabled = false; continueBtn.textContent = defaultLabel;
      }
    });

    setTimeout(() => nameEl.focus(), 50);
  }

  // Intercept any cart checkout button. Capture phase + preventDefault so the
  // native form submit (straight to checkout) is blocked until we decide.
  document.addEventListener('click', async (e) => {
    if (proceeding) return;
    const btn = e.target.closest('#checkout, [name="checkout"], .cart__checkout-button');
    if (!btn) return;
    e.preventDefault();
    e.stopPropagation();
    try {
      const cart = await getCart();
      const needs = (cart.items || []).some((i) => !i.gift_card);
      if (!needs || hasStudent(cart.attributes)) {
        goToCheckout();
        return;
      }
      buildModal(cart.attributes, 'checkout');
    } catch (err) {
      // On any failure, don't trap the user — let them check out.
      goToCheckout();
    }
  }, true);

  // "Edit" link in the cart student summary — reopen the modal pre-filled,
  // saving in place (no auto-checkout).
  document.addEventListener('click', async (e) => {
    const editBtn = e.target.closest('[data-edit-student]');
    if (!editBtn) return;
    e.preventDefault();
    e.stopPropagation();
    try {
      const cart = await getCart();
      buildModal(cart.attributes, 'edit');
    } catch (err) {
      // Still let the family enter details rather than failing silently.
      buildModal(null, 'edit');
    }
  }, true);
})();
